<template>
  <div class="workbook-shell">
    <div class="workbook-stage">
      <div
        v-for="sheet in sheets"
        :key="sheet.id"
        v-show="activeSheetId === sheet.id"
        class="workbook-stage__sheet"
      >
        <HermesSpreadsheetComponentAdvanced
          :ref="(el) => registerSheetRef(sheet.id, el)"
          :button-label="buttonLabel"
          width="100%"
          height="100%"
          :highlight-header="highlightHeader"
          :freeze-top-row="false"
          :auto-save-enabled="autoSaveEnabled"
          :stretch-columns="stretchColumns"
          :show-add-rows-button="showAddRowsButton"
          :enable-formulas="enableFormulas"
          :keep-selection-selectors="resolvedKeepSelectionSelectors"
          :custom-context-menu-items="buildSheetCustomContextMenuItems(sheet.id)"
          @request-save="handleSheetSaveRequest"
          @update-last-selection="handleSheetSelectionUpdate(sheet.id, $event)"
          @deselected="handleSheetDeselection(sheet.id)"
          @context-menu-cell="handleSheetContextMenuCell(sheet.id, $event)"
          @ready="handleSheetReady(sheet.id)"
        />
      </div>
    </div>

    <div class="sheet-tabs" role="tablist" aria-label="Input workbook tabs">
      <div class="sheet-tabs__scroller">
        <div
          v-for="sheet in sheets"
          :key="`${sheet.id}-tab`"
          class="sheet-tab"
          :class="{
            'sheet-tab--active': activeSheetId === sheet.id,
            'sheet-tab--renaming': renamingSheetId === sheet.id
          }"
          @contextmenu.prevent.stop="openSheetContextMenu($event, sheet.id)"
        >
          <button
            v-if="renamingSheetId !== sheet.id"
            class="sheet-tab__main"
            type="button"
            role="tab"
            :aria-selected="activeSheetId === sheet.id"
            @click="activateSheet(sheet.id)"
            @dblclick.stop="beginRenameSheet(sheet.id)"
            :title="`${sheet.name} • Double-click to rename`"
          >
            <span class="sheet-tab__name">{{ sheet.name }}</span>
          </button>

          <div v-else class="sheet-tab__rename">
            <input
              :ref="(el) => registerRenameInputRef(sheet.id, el)"
              v-model="renameDraft"
              class="sheet-tab__rename-input"
              type="text"
              maxlength="32"
              spellcheck="false"
              @keydown.enter.prevent="commitRenameSheet"
              @keydown.esc.prevent="cancelRenameSheet"
              @blur="commitRenameSheet"
            />
          </div>

          <button
            class="sheet-tab__close"
            type="button"
            :disabled="sheets.length === 1"
            :title="sheets.length === 1 ? 'At least one sheet is required' : `Delete ${sheet.name}`"
            @click.stop="confirmRemoveSheet(sheet.id)"
          >
            ×
          </button>
        </div>
      </div>

      <button
        class="sheet-tab sheet-tab--add"
        type="button"
        :disabled="sheets.length >= maxSheets"
        title="Add a new sheet"
        @click="addSheet"
      >
        +
      </button>
    </div>

    <teleport to="body">
      <div
        v-if="sheetContextMenu.open"
        class="sheet-tab-context-menu"
        :style="sheetContextMenuStyle"
        @contextmenu.prevent
      >
        <button
          class="sheet-tab-context-menu__item"
          type="button"
          :disabled="sheets.length >= maxSheets"
          @click="duplicateSheetFromContextMenu"
        >
          Duplicate
        </button>
        <button
          class="sheet-tab-context-menu__item"
          type="button"
          @click="renameSheetFromContextMenu"
        >
          Rename
        </button>
        <button
          class="sheet-tab-context-menu__item sheet-tab-context-menu__item--danger"
          type="button"
          :disabled="sheets.length === 1"
          @click="deleteSheetFromContextMenu"
        >
          Delete
        </button>
      </div>
    </teleport>
  </div>
</template>

<script>
import HermesSpreadsheetComponentAdvanced from './HermesSpreadsheetComponentAdvanced.vue';

const WORKBOOK_VERSION = 1;


function isWorkbookStorageQuotaError(error) {
  const name = String(error?.name || '');
  const message = String(error?.message || '');
  return name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED' || /quota|storage/i.test(message);
}

function estimateWorkbookStorageUsage() {
  const result = {
    supported: false,
    usedBytes: 0,
    itemCount: 0
  };

  try {
    if (typeof localStorage === 'undefined') return result;
    result.supported = true;
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index) || '';
      const value = localStorage.getItem(key) || '';
      result.itemCount += 1;
      result.usedBytes += (key.length + value.length) * 2;
    }
  } catch {
    // Best-effort display only.
  }

  return result;
}

function createSheetId() {
  return `sheet-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function cloneSelection(selection) {
  return Array.isArray(selection?.[0])
    ? selection.map(coords => coords.slice(0, 4))
    : null;
}

export default {
  name: 'HermesSpreadsheetWorkbookTabs',
  components: {
    HermesSpreadsheetComponentAdvanced
  },
  emits: ['request-save', 'update-last-selection', 'deselected', 'ready', 'active-sheet-change', 'context-menu-cell', 'workbook-storage-error'],
  props: {
    buttonLabel: { type: String, default: 'Add 100 Rows' },
    width: { type: [String, Number], default: '100%' },
    height: { type: [String, Number], default: '100%' },
    highlightHeader: { type: Boolean, default: false },
    autoSaveEnabled: { type: Boolean, default: true },
    stretchColumns: { type: String, default: 'last' },
    showAddRowsButton: { type: Boolean, default: true },
    enableFormulas: { type: Boolean, default: false },
    keepSelectionSelectors: {
      type: Array,
      default: () => ['.sheet-toolbar', '.modal-overlay', '.htMenu', '.htBorders']
    },
    customContextMenuItems: {
      type: [Object, Array, Function],
      default: null
    },
    maxSheets: { type: Number, default: 8 },
    storageKey: { type: String, default: 'tool60_input_workbook_state_v4' },
    legacyStorageKeys: {
      type: Array,
      default: () => ['tool60_state']
    }
  },
  data() {
    return {
      sheets: [],
      activeSheetId: null,
      renamingSheetId: null,
      renameDraft: '',
      sheetRefs: {},
      renameInputRefs: {},
      pendingSheetStates: {},
      selectionBySheetId: {},
      sheetContextMenu: {
        open: false,
        sheetId: null,
        x: 0,
        y: 0
      },
      lastWorkbookSaveResult: null
    };
  },
  computed: {
    hotInstance() {
      return this.getActiveHot();
    },
    cellClassMap() {
      return this.getActiveSheet()?.cellClassMap || new Map();
    },
    lastSelection() {
      return cloneSelection(this.selectionBySheetId[this.activeSheetId] || this.getActiveSheet()?.lastSelection);
    },
    activeSheetName() {
      return this.getSheetMeta(this.activeSheetId)?.name || 'Sheet 1';
    },
    sheetContextMenuStyle() {
      return {
        left: `${this.sheetContextMenu.x}px`,
        top: `${this.sheetContextMenu.y}px`
      };
    },
    resolvedKeepSelectionSelectors() {
      const selectors = Array.isArray(this.keepSelectionSelectors) ? this.keepSelectionSelectors : [];
      return Array.from(
        new Set([
          ...selectors,
          '.sheet-tabs',
          '.sheet-tab',
          '.sheet-tab__main',
          '.sheet-tab__close',
          '.sheet-tab__rename',
          '.sheet-tab__rename-input',
          '.sheet-tab-context-menu',
          '.sheet-tab-context-menu__item'
        ])
      );
    }
  },
  created() {
    this.initializeWorkbookModel();
  },
  mounted() {
    this.$nextTick(() => {
      this.emitActiveSheetContext();
      this.getActiveSheet()?.refreshDimensions?.();
    });

    this.handleGlobalPointerDown = (event) => {
      if (!this.sheetContextMenu.open) {
        return;
      }

      if (!(event.target instanceof Element) || !event.target.closest('.sheet-tab-context-menu')) {
        this.closeSheetContextMenu();
      }
    };

    this.handleGlobalKeyDown = (event) => {
      if (event.key === 'Escape' && this.sheetContextMenu.open) {
        this.closeSheetContextMenu();
      }
    };

    document.addEventListener('pointerdown', this.handleGlobalPointerDown, true);
    document.addEventListener('keydown', this.handleGlobalKeyDown, true);
  },
  beforeUnmount() {
    document.removeEventListener('pointerdown', this.handleGlobalPointerDown, true);
    document.removeEventListener('keydown', this.handleGlobalKeyDown, true);
  },
  methods: {
    getRangeData(startRow, startCol, endRow, endCol) {
      return this.getActiveSheet()?.getRangeData?.(startRow, startCol, endRow, endCol) ?? [];
    },

    setRangeInput(startRow, startCol, matrix, source = 'agent_panel') {
      return this.getActiveSheet()?.setRangeInput?.(startRow, startCol, matrix, source)
        ?? { ok: false, status: 'Active sheet unavailable.' };
    },

    resolveCustomContextMenuItemsForSheet(sheetId) {
      if (!this.customContextMenuItems) {
        return null;
      }

      if (typeof this.customContextMenuItems === 'function') {
        return (payload = {}) => this.customContextMenuItems({
          ...payload,
          sheetId,
          sheetName: this.getSheetMeta(sheetId)?.name || ''
        });
      }

      return this.customContextMenuItems;
    },

    initializeWorkbookModel() {
      const restored = this.readSavedWorkbook();

      if (restored?.sheets?.length) {
        this.sheets = restored.sheets.map(({ id, name }) => ({ id, name }));
        this.pendingSheetStates = restored.sheets.reduce((accumulator, sheet) => {
          accumulator[sheet.id] = sheet.state || null;
          return accumulator;
        }, {});
        this.sheets.forEach(sheet => this.ensureSelectionEntry(sheet.id));
        this.activeSheetId = this.sheets.some(sheet => sheet.id === restored.activeSheetId)
          ? restored.activeSheetId
          : this.sheets[0].id;
        return;
      }

      const defaultSheet = this.createSheetMeta('Sheet 1');
      this.sheets = [defaultSheet];
      this.activeSheetId = defaultSheet.id;
      this.ensureSelectionEntry(defaultSheet.id);
    },

    createSheetMeta(name = null) {
      return {
        id: createSheetId(),
        name: name || this.nextDefaultSheetName()
      };
    },

    nextDefaultSheetName() {
      const usedNames = new Set(this.sheets.map(sheet => String(sheet.name || '').trim()));
      let index = 1;

      while (usedNames.has(`Sheet ${index}`)) {
        index += 1;
      }

      return `Sheet ${index}`;
    },

  cloneSerializableState(state) {
  const structuredCloneFn =
    typeof window !== 'undefined' && typeof window.structuredClone === 'function'
      ? window.structuredClone
      : null;

  if (structuredCloneFn) {
    return structuredCloneFn(state);
  }

  return JSON.parse(JSON.stringify(state));
},

    buildDuplicateSheetName(sourceName = 'Sheet') {
      const baseName = String(sourceName || 'Sheet').trim().replace(/ Copy(?: \d+)?$/, '');
      const usedNames = new Set(this.sheets.map(sheet => String(sheet.name || '').trim()));
      let duplicateName = `${baseName} Copy`;
      let index = 2;

      while (usedNames.has(duplicateName)) {
        duplicateName = `${baseName} Copy ${index}`;
        index += 1;
      }

      return duplicateName;
    },

    parseJson(raw) {
      try {
        return JSON.parse(raw);
      } catch (error) {
        console.warn('Failed to parse workbook state:', error);
        return null;
      }
    },

    readSavedWorkbook() {
      const workbookRaw = localStorage.getItem(this.storageKey);
      if (workbookRaw) {
        const parsed = this.parseJson(workbookRaw);
        if (this.isWorkbookState(parsed)) {
          return this.normalizeWorkbookState(parsed);
        }
      }

      for (const legacyKey of this.legacyStorageKeys) {
        const legacyRaw = localStorage.getItem(legacyKey);
        if (!legacyRaw) {
          continue;
        }

        const legacyState = this.parseJson(legacyRaw);
        if (legacyState && Array.isArray(legacyState.data)) {
          const sheet = this.createSheetMeta('Sheet 1');
          return {
            version: WORKBOOK_VERSION,
            activeSheetId: sheet.id,
            sheets: [
              {
                id: sheet.id,
                name: sheet.name,
                state: legacyState
              }
            ]
          };
        }
      }

      return null;
    },

    isWorkbookState(candidate) {
      return !!candidate && Array.isArray(candidate.sheets) && candidate.sheets.length > 0;
    },

    normalizeWorkbookState(workbook) {
      const sheets = workbook.sheets.map((sheet, index) => ({
        id: typeof sheet.id === 'string' && sheet.id ? sheet.id : createSheetId(),
        name: String(sheet.name || `Sheet ${index + 1}`),
        state: sheet.state || null
      }));

      const activeSheetId = sheets.some(sheet => sheet.id === workbook.activeSheetId)
        ? workbook.activeSheetId
        : sheets[0].id;

      return {
        version: WORKBOOK_VERSION,
        activeSheetId,
        sheets
      };
    },

    ensureSelectionEntry(sheetId) {
      if (!Object.prototype.hasOwnProperty.call(this.selectionBySheetId, sheetId)) {
        this.selectionBySheetId[sheetId] = null;
      }
    },

    registerSheetRef(sheetId, componentInstance) {
      if (componentInstance) {
        this.sheetRefs[sheetId] = componentInstance;
      } else {
        delete this.sheetRefs[sheetId];
      }
    },

    registerRenameInputRef(sheetId, element) {
      if (element) {
        this.renameInputRefs[sheetId] = element;
      } else {
        delete this.renameInputRefs[sheetId];
      }
    },

    getSheetMeta(sheetId) {
      return this.sheets.find(sheet => sheet.id === sheetId) || null;
    },

    getSheetRef(sheetId) {
      return this.sheetRefs[sheetId] || null;
    },

    getActiveSheet() {
      return this.getSheetRef(this.activeSheetId);
    },

    getActiveHot() {
      return this.getActiveSheet()?.hotInstance || null;
    },

    getUndoRedoPlugin() {
      const activeSheet = this.getActiveSheet();
      const hot = this.getActiveHot();
      return activeSheet?.getUndoRedoPlugin?.() || hot?.getPlugin?.('undoRedo') || hot?.undoRedo || null;
    },

    clearSearchOnHot(hot) {
      const searchPlugin = hot?.getPlugin?.('search');
      if (searchPlugin?.query) {
        searchPlugin.query('');
        hot.render?.();
      }
    },

    emitActiveSheetContext() {
      const activeSheet = this.getActiveSheet();
      const selection = cloneSelection(this.selectionBySheetId[this.activeSheetId] || activeSheet?.lastSelection);

      this.$emit('update-last-selection', selection);
      if (!selection) {
        this.$emit('deselected');
      }

      this.$emit('active-sheet-change', {
        sheetId: this.activeSheetId,
        sheetName: this.activeSheetName,
        hotInstance: activeSheet?.hotInstance || null
      });

      if (activeSheet?.hotInstance) {
        this.$emit('ready', activeSheet.hotInstance);
      }
    },

    handleSheetReady(sheetId) {
      const sheet = this.getSheetRef(sheetId);
      if (!sheet) {
        return;
      }

      const pendingState = this.pendingSheetStates[sheetId];
      if (pendingState) {
        try {
          sheet.applySheetState?.(pendingState);
        } catch (error) {
          console.error('Failed to restore saved sheet state. Falling back to a blank sheet.', error);
          sheet.resetSheet?.();
        } finally {
          delete this.pendingSheetStates[sheetId];
        }
      } else {
        sheet.clearUndoRedoHistory?.();
      }

      if (sheetId === this.activeSheetId) {
        this.$nextTick(() => {
          sheet.refreshDimensions?.();
          this.emitActiveSheetContext();
        });
      }
    },

    handleSheetSaveRequest() {
      if (this.autoSaveEnabled) {
        this.$emit('request-save');
      }
    },

    handleSheetSelectionUpdate(sheetId, selection) {
      this.selectionBySheetId[sheetId] = cloneSelection(selection);

      if (sheetId === this.activeSheetId) {
        this.$emit('update-last-selection', this.selectionBySheetId[sheetId]);
      }
    },

    handleSheetDeselection(sheetId) {
      this.selectionBySheetId[sheetId] = null;

      if (sheetId === this.activeSheetId) {
        this.$emit('deselected');
      }
    },

    buildSheetCustomContextMenuItems(sheetId) {
      const sheet = this.getSheetMeta(sheetId);

      return (payload = {}) => {
        const enrichedPayload = {
          ...payload,
          sheetId,
          sheetName: sheet?.name || '',
          activeSheetId: this.activeSheetId,
          activeSheetName: this.activeSheetName,
          isActiveSheet: sheetId === this.activeSheetId
        };

        if (typeof this.customContextMenuItems === 'function') {
          return this.customContextMenuItems(enrichedPayload);
        }

        return this.customContextMenuItems;
      };
    },


    handleSheetContextMenuCell(sheetId, payload = {}) {
      this.ensureSelectionEntry(sheetId);
      const selection = cloneSelection(payload.selection);
      if (selection) {
        this.selectionBySheetId[sheetId] = selection;
      }

      if (sheetId !== this.activeSheetId) {
        return;
      }

      this.$emit('context-menu-cell', {
        ...payload,
        sheetId,
        sheetName: this.getSheetMeta(sheetId)?.name || '',
        selection: selection || null
      });
    },

    activateSheet(sheetId) {
      if (!sheetId) {
        return;
      }

      this.closeSheetContextMenu();

      if (sheetId === this.activeSheetId) {
        this.getActiveSheet()?.refreshDimensions?.();
        return;
      }

      const previousHot = this.getActiveHot();
      if (previousHot) {
        this.clearSearchOnHot(previousHot);
      }

      this.activeSheetId = sheetId;

      this.$nextTick(() => {
        const activeSheet = this.getActiveSheet();
        const activeHot = this.getActiveHot();

        if (activeHot) {
          this.clearSearchOnHot(activeHot);
        }

        activeSheet?.refreshDimensions?.();
        this.emitActiveSheetContext();
      });
    },

    beginRenameSheet(sheetId) {
      this.closeSheetContextMenu();
      this.renamingSheetId = sheetId;
      this.renameDraft = this.getSheetMeta(sheetId)?.name || '';

      this.$nextTick(() => {
        const input = this.renameInputRefs[sheetId];
        input?.focus?.();
        input?.select?.();
      });
    },

    commitRenameSheet() {
      if (!this.renamingSheetId) {
        return;
      }

      const meta = this.getSheetMeta(this.renamingSheetId);
      const trimmed = this.renameDraft.trim().slice(0, 32);

      if (meta && trimmed) {
        meta.name = trimmed;
      }

      this.renamingSheetId = null;
      this.renameDraft = '';
      this.emitActiveSheetContext();

      if (this.autoSaveEnabled) {
        this.$emit('request-save');
      }
    },

    cancelRenameSheet() {
      this.renamingSheetId = null;
      this.renameDraft = '';
    },

    addSheet() {
      this.closeSheetContextMenu();

      if (this.sheets.length >= this.maxSheets) {
        return;
      }

      const nextSheet = this.createSheetMeta();
      this.sheets.push(nextSheet);
      this.ensureSelectionEntry(nextSheet.id);
      this.pendingSheetStates[nextSheet.id] = null;

      this.activateSheet(nextSheet.id);

      this.$nextTick(() => {
        this.beginRenameSheet(nextSheet.id);
      });

      if (this.autoSaveEnabled) {
        this.$emit('request-save');
      }
    },

    confirmRemoveSheet(sheetId) {
      this.closeSheetContextMenu();

      if (this.sheets.length <= 1) {
        window.alert('At least one sheet is required.');
        return;
      }

      const meta = this.getSheetMeta(sheetId);
      if (!meta) {
        return;
      }

      const confirmed = window.confirm(`Delete "${meta.name}" and all of its data? This cannot be undone.`);
      if (!confirmed) {
        return;
      }

      const index = this.sheets.findIndex(sheet => sheet.id === sheetId);
      const removingActiveSheet = sheetId === this.activeSheetId;

      this.sheets.splice(index, 1);
      delete this.sheetRefs[sheetId];
      delete this.renameInputRefs[sheetId];
      delete this.pendingSheetStates[sheetId];
      delete this.selectionBySheetId[sheetId];

      if (this.renamingSheetId === sheetId) {
        this.cancelRenameSheet();
      }

      if (removingActiveSheet) {
        const fallbackSheet = this.sheets[Math.max(0, index - 1)] || this.sheets[0] || null;
        this.activeSheetId = fallbackSheet?.id || null;
      }

      this.$nextTick(() => {
        this.getActiveSheet()?.refreshDimensions?.();
        this.emitActiveSheetContext();
      });

      if (this.autoSaveEnabled) {
        this.$emit('request-save');
      }
    },

    openSheetContextMenu(event, sheetId) {
      if (!sheetId) {
        return;
      }

      if (this.activeSheetId !== sheetId) {
        this.activeSheetId = sheetId;
        this.$nextTick(() => {
          this.getActiveSheet()?.refreshDimensions?.();
          this.emitActiveSheetContext();
        });
      }

      const menuWidth = 184;
      const menuHeight = 144;
      const clientX = Number.isFinite(event?.clientX) ? event.clientX : 12;
      const clientY = Number.isFinite(event?.clientY) ? event.clientY : 12;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const maxX = Math.max(12, viewportWidth - menuWidth - 12);
      const maxY = Math.max(12, viewportHeight - menuHeight - 12);

      this.sheetContextMenu = {
        open: true,
        sheetId,
        x: Math.min(Math.max(12, clientX), maxX),
        y: Math.min(Math.max(12, clientY), maxY)
      };
    },

    closeSheetContextMenu() {
      if (!this.sheetContextMenu.open) {
        return;
      }

      this.sheetContextMenu = {
        open: false,
        sheetId: null,
        x: 0,
        y: 0
      };
    },

    duplicateSheet(sheetId) {
      if (!sheetId || this.sheets.length >= this.maxSheets) {
        return;
      }

      const sourceMeta = this.getSheetMeta(sheetId);
      if (!sourceMeta) {
        return;
      }

      const duplicateSheetMeta = {
        id: createSheetId(),
        name: this.buildDuplicateSheetName(sourceMeta.name)
      };
      const sourceState = this.getSheetRef(sheetId)?.getSheetState?.() || this.pendingSheetStates[sheetId] || null;
      const duplicateState = sourceState ? this.cloneSerializableState(sourceState) : null;
      const sourceSelection = cloneSelection(this.selectionBySheetId[sheetId]);
      const sourceIndex = this.sheets.findIndex(sheet => sheet.id === sheetId);
      const insertIndex = sourceIndex >= 0 ? sourceIndex + 1 : this.sheets.length;

      this.sheets.splice(insertIndex, 0, duplicateSheetMeta);
      this.ensureSelectionEntry(duplicateSheetMeta.id);
      this.selectionBySheetId[duplicateSheetMeta.id] = sourceSelection;
      this.pendingSheetStates[duplicateSheetMeta.id] = duplicateState;
      this.activateSheet(duplicateSheetMeta.id);

      if (this.autoSaveEnabled) {
        this.$emit('request-save');
      }
    },

    duplicateSheetFromContextMenu() {
      const sheetId = this.sheetContextMenu.sheetId;
      this.closeSheetContextMenu();
      this.duplicateSheet(sheetId);
    },

    renameSheetFromContextMenu() {
      const sheetId = this.sheetContextMenu.sheetId;
      this.closeSheetContextMenu();
      if (sheetId) {
        this.beginRenameSheet(sheetId);
      }
    },

    deleteSheetFromContextMenu() {
      const sheetId = this.sheetContextMenu.sheetId;
      this.closeSheetContextMenu();
      if (sheetId) {
        this.confirmRemoveSheet(sheetId);
      }
    },

    getCellSourceValue(row, col) {
      return this.getActiveSheet()?.getCellSourceValue?.(row, col) ?? '';
    },

    getCellInput(row, col) {
      return this.getActiveSheet()?.getCellInput?.(row, col)
        ?? this.getActiveSheet()?.getCellSourceValue?.(row, col)
        ?? '';
    },

    setCellValue(row, col, value, source = 'edit') {
      return this.getActiveSheet()?.setCellValue?.(row, col, value, source) ?? false;
    },

    setCellInput(row, col, value, source = 'formula_bar') {
      return this.getActiveSheet()?.setCellInput?.(row, col, value, source)
        ?? this.getActiveSheet()?.setCellValue?.(row, col, value, source)
        ?? false;
    },

    selectCell(row, col, options = {}) {
      return this.getActiveSheet()?.selectCell?.(row, col, options) ?? false;
    },

    runUndoableClassChange(label, selection, mutation) {
      const activeSheet = this.getActiveSheet();
      if (activeSheet?.runUndoableClassChange) {
        return activeSheet.runUndoableClassChange(label, selection, mutation);
      }

      if (typeof mutation === 'function') {
        mutation();
      }

      return true;
    },

    runUndoableFullClassChange(label, mutation) {
      const activeSheet = this.getActiveSheet();
      if (activeSheet?.runUndoableFullClassChange) {
        return activeSheet.runUndoableFullClassChange(label, mutation);
      }

      if (typeof mutation === 'function') {
        mutation();
      }

      return true;
    },

    runUndoableBorderChange(label, selection, mutation) {
      const activeSheet = this.getActiveSheet();
      if (activeSheet?.runUndoableBorderChange) {
        return activeSheet.runUndoableBorderChange(label, selection, mutation);
      }

      if (typeof mutation === 'function') {
        mutation();
      }

      return true;
    },

    restoreFormatting() {
      this.getActiveSheet()?.restoreFormatting?.();
    },

    clearRecolors() {
      this.getActiveSheet()?.clearRecolors?.();
    },

    refreshDimensions() {
      this.getActiveSheet()?.refreshDimensions?.();
    },

    resetRowDisplayToDefault() {
      return this.getActiveSheet()?.resetRowDisplayToDefault?.()
        ?? this.getActiveSheet()?.resetRowHeightsToDefault?.()
        ?? false;
    },

    resetRowHeightsToDefault() {
      return this.resetRowDisplayToDefault();
    },

    resetSheet(...args) {
      const result = this.getActiveSheet()?.resetSheet?.(...args)
        ?? { ok: false, status: 'Active sheet unavailable.' };

      this.selectionBySheetId[this.activeSheetId] = null;
      this.emitActiveSheetContext();
      return result;
    },

    getData() {
      return this.getActiveSheet()?.getData?.() || [];
    },

    loadData(data) {
      this.getActiveSheet()?.loadData?.(data);
    },

    getSheetState() {
      return this.getActiveSheet()?.getSheetState?.() || null;
    },

    applySheetState(state) {
      return this.getActiveSheet()?.applySheetState?.(state) || null;
    },

    buildWorkbookState() {
      return {
        version: WORKBOOK_VERSION,
        activeSheetId: this.activeSheetId,
        sheets: this.sheets.map((sheetMeta) => ({
          id: sheetMeta.id,
          name: sheetMeta.name,
          state: this.getSheetRef(sheetMeta.id)?.getSheetState?.() || this.pendingSheetStates[sheetMeta.id] || null
        }))
      };
    },

    saveWorkbook(storageKey = this.storageKey) {
      const workbook = this.buildWorkbookState();
      let saveResult = {
        ok: true,
        storageKey: storageKey || '',
        bytes: 0,
        quota: false,
        errorName: '',
        message: ''
      };

      if (storageKey) {
        let serialized = '';
        try {
          serialized = JSON.stringify(workbook);
          saveResult = {
            ...saveResult,
            bytes: serialized.length * 2
          };
          localStorage.setItem(storageKey, serialized);
        } catch (error) {
          saveResult = {
            ok: false,
            storageKey,
            bytes: serialized ? serialized.length * 2 : 0,
            quota: isWorkbookStorageQuotaError(error),
            errorName: String(error?.name || 'StorageError'),
            message: error?.message || 'Could not save workbook to browser storage.',
            storageEstimate: estimateWorkbookStorageUsage()
          };
          console.warn('Workbook save failed.', saveResult, error);
          this.$emit('workbook-storage-error', saveResult);
        }
      }

      this.lastWorkbookSaveResult = saveResult;
      try {
        Object.defineProperty(workbook, '__storageSaveResult', {
          value: saveResult,
          enumerable: false,
          configurable: true
        });
      } catch {
        workbook.__storageSaveResult = saveResult;
      }

      return workbook;
    }
  }
};
</script>

<style scoped>
.workbook-shell {
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
}
.workbook-stage {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.workbook-stage__sheet {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
}

.sheet-tabs {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.08rem;
  width: 100%;
  min-width: 0;
  min-height: 1.18rem;
  overflow: hidden;
  padding: 0.02rem 0;
}

.sheet-tabs__scroller {
  display: inline-flex;
  align-items: center;
  gap: 0.08rem;
  width: fit-content;
  max-width: calc(100% - 1.42rem);
  min-width: 0;
  flex: 0 1 auto;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0;
  scrollbar-width: thin;
}

.sheet-tabs__scroller::-webkit-scrollbar {
  height: 4px;
}

.sheet-tab {
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  flex: 0 0 auto;
  min-width: 0;
  height: 1.18rem;
  max-width: 9.5rem;
  border: 1px solid #94a3b8;
  border-bottom-color: #64748b;
  border-radius: 6px;
  background: #f8fafc;
  box-shadow: none;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;
}

.sheet-tab:hover {
  border-color: #64748b;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
}

.sheet-tab--active {
  border-color: #2563eb;
  border-bottom-color: #1d4ed8;
  background: #ffffff;
  box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.12);
}

.sheet-tab__main {
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
  height: 100%;
  min-height: 0;
  border: none;
  background: transparent;
  color: #0f172a;
  padding: 0 0.36rem;
  cursor: pointer;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1;
}

.sheet-tab__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sheet-tab__rename {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 0.12rem 0 0.24rem;
}

.sheet-tab__rename-input {
  box-sizing: border-box;
  width: 7.75rem;
  max-width: 100%;
  height: 1rem;
  min-height: 0;
  border: 1px solid #2563eb;
  border-radius: 5px;
  background: #ffffff;
  color: #0f172a;
  padding: 0 0.26rem;
  font-size: 0.68rem;
  font-weight: 600;
  line-height: 1;
}

.sheet-tab__close {
  box-sizing: border-box;
  height: 100%;
  min-height: 0;
  border: none;
  border-left: 1px solid rgba(148, 163, 184, 0.72);
  background: transparent;
  color: #64748b;
  padding: 0 0.26rem;
  cursor: pointer;
  font-size: 0.74rem;
  line-height: 1;
}

.sheet-tab__close:hover:not(:disabled) {
  color: #ef4444;
}

.sheet-tab__close:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.sheet-tab--add {
  margin-left: 0;
  flex: 0 0 auto;
  justify-content: center;
  width: 1.18rem;
  min-width: 1.18rem;
  height: 1.18rem;
  max-width: none;
  padding: 0;
  border: 1px solid #2563eb;
  border-radius: 6px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.82rem;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
}
.sheet-tab--add:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.sheet-tab-context-menu {
  position: fixed;
  z-index: 1400;
  min-width: 10.5rem;
  padding: 0.35rem;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.18);
  backdrop-filter: blur(12px);
}

.sheet-tab-context-menu__item {
  display: block;
  width: 100%;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #0f172a;
  padding: 0.65rem 0.75rem;
  text-align: left;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
}

.sheet-tab-context-menu__item:hover:not(:disabled) {
  background: #eff6ff;
}

.sheet-tab-context-menu__item:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.sheet-tab-context-menu__item--danger:hover:not(:disabled) {
  background: #fef2f2;
  color: #b91c1c;
}

@media (max-width: 720px) {
  .sheet-tab {
    max-width: 8.5rem;
  }

  .sheet-tab__main {
    padding-inline: 0.32rem;
  }
}
</style>
